/**
 * KAN-755: промокод заказа. Без промокода: поле и "Применить" (код в
 * верхнем регистре). С промокодом: чип с крестиком (снять) и сообщение по
 * promo_status. Ошибка 400 (не найден или не действует) текстом под полем.
 * Показывается только в заказе, который можно редактировать.
 */
import { useState } from 'react';
import { Loader2, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { OrderPromoStatus } from '@/types/order';

const PROMO_STATUS_TEXT: Record<OrderPromoStatus, string> = {
  applied: 'Промокод применён',
  not_better: 'Уже действует большая скидка',
  no_items: 'Промокод не подходит к товарам корзины',
};

interface OrderPromoCodeProps {
  promoCode: string | null | undefined;
  promoStatus: OrderPromoStatus | null | undefined;
  pending: boolean;
  error: unknown;
  onApply: (code: string) => void;
  onRemove: () => void;
}

export function OrderPromoCode({ promoCode, promoStatus, pending, error, onApply, onRemove }: OrderPromoCodeProps) {
  const [code, setCode] = useState('');
  const trimmed = code.trim();
  const statusText = promoStatus ? PROMO_STATUS_TEXT[promoStatus] : null;

  return (
    <div className="space-y-1.5" data-testid="order-promo">
      {promoCode ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">Промокод</span>
          <span
            className="inline-flex items-center gap-1 rounded-full bg-green-50 py-0.5 pl-2.5 pr-1 font-mono text-sm text-green-800"
            data-testid="order-promo-chip"
          >
            <Tag className="h-3.5 w-3.5" />
            {promoCode}
            <button
              type="button"
              aria-label="Снять промокод"
              disabled={pending}
              className="rounded-full p-0.5 hover:bg-green-100 disabled:opacity-50"
              onClick={onRemove}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
          {statusText && (
            <span
              className={promoStatus === 'applied' ? 'text-sm text-green-700' : 'text-sm text-amber-700'}
              data-testid="order-promo-status"
            >
              {statusText}
            </span>
          )}
        </div>
      ) : (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed) onApply(trimmed.toUpperCase());
          }}
        >
          <Input
            aria-label="Промокод"
            placeholder="Промокод"
            autoComplete="off"
            className="h-9 w-44 font-mono uppercase"
            value={code}
            disabled={pending}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          <Button type="submit" variant="outline" size="sm" disabled={pending || !trimmed}>
            {pending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Применить
          </Button>
        </form>
      )}
      {error ? (
        <p role="alert" className="text-sm text-red-700" data-testid="order-promo-error">
          {extractErrorMessage(error, 'Не удалось применить промокод')}
        </p>
      ) : null}
    </div>
  );
}
